import type {
	TopologyFaceReferenceSourceDescriptor,
} from '../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';
import type {
	LineGeomFingerprint,
	CircleGeomFingerprint,
	ArcGeomFingerprint,
	GeomFingerprint,
	TopologySourceOwnerKind,
	TopologySourceOwnerDescriptor,
	TopologyEdgeResolutionLevel,
	TopologySampledCurveFitKind,
	TopologySampledCurveFitMethod,
	TopologySampledCurveFitMetadata,
	TopologyEdgeMatchHints,
	TopologyEdgeSourceLocator,
	FeatureEdgeSourceLocator,
	SketchEntitySourceDescriptor,
	SketchEntitySourceSubEntityLocator,
	FeatureEdgeSourceDescriptor,
	TopologyEdgeSourceDescriptor,
	ExternalGeometrySourceDescriptor,
} from '../../../../types/reference/sketch-entity-reference/ExternalGeometrySourceDescriptor';

export type {
	LineGeomFingerprint,
	CircleGeomFingerprint,
	ArcGeomFingerprint,
	GeomFingerprint,
	TopologySourceOwnerKind,
	TopologySourceOwnerDescriptor,
	TopologyEdgeResolutionLevel,
	TopologySampledCurveFitKind,
	TopologySampledCurveFitMethod,
	TopologySampledCurveFitMetadata,
	TopologyEdgeMatchHints,
	TopologyEdgeSourceLocator,
	FeatureEdgeSourceLocator,
	SketchEntitySourceDescriptor,
	SketchEntitySourceSubEntityLocator,
	FeatureEdgeSourceDescriptor,
	TopologyEdgeSourceDescriptor,
	ExternalGeometrySourceDescriptor,
} from '../../../../types/reference/sketch-entity-reference/ExternalGeometrySourceDescriptor';

/**
 * 解析 topology owner 的稳定身份 key。
 *
 * @param owner topology 来源 owner。
 * @returns 可用于 key / group / 查询对比的 owner 身份。
 */
export function resolveTopologySourceOwnerIdentityKey(
	owner: TopologySourceOwnerDescriptor,
): string {
	const bodyKey = owner.bodyBindingId ? `::${owner.bodyBindingId}` : '';
	const modelBodyKey = owner.modelBodyId ? `::model-body:${owner.modelBodyId}` : '';
	const modelBodyRevisionKey = owner.modelBodyRevisionId
		? `::model-body-revision:${owner.modelBodyRevisionId}`
		: '';
	return `${owner.ownerKind}::${owner.ownerId}${bodyKey}${modelBodyKey}${modelBodyRevisionKey}`;
}

/**
 * 构建 topology vertex 在逻辑来源 owner 内的稳定端点锚点 ID。
 *
 * @param owner topology 来源 owner。
 * @param vertexReference canonical topology vertex 引用。
 * @returns 可跨选择、创建和重建链复用的端点锚点 ID。
 */
export function resolveTopologyEndpointAnchorId(
	owner: TopologySourceOwnerDescriptor,
	vertexReference: string,
): string {
	return `topology:${resolveTopologySourceOwnerIdentityKey(owner)}:vertex:${vertexReference}`;
}

/**
 * 解析 topology 来源在当前结果快照中的成员作用域。
 *
 * 逻辑 owner 用于跨重建关联，而面域连通与局部拓扑地址只在单个结果成员内有意义；
 * 因此仅在当前结果对象存在时追加成员 ID，旧数据仍回退逻辑 owner 身份。
 *
 * @param owner topology 来源 owner。
 * @returns 可隔离局部拓扑地址的结果成员作用域。
 */
export function resolveTopologySourceMemberScopeKey(
	owner: TopologySourceOwnerDescriptor,
): string {
	const ownerKey = resolveTopologySourceOwnerIdentityKey(owner);
	return owner.resultObjectId
		? `${ownerKey}::result-member:${owner.resultObjectId}`
		: ownerKey;
}

/**
 * 解析 topology 面来源的稳定会话身份。
 *
 * 该身份用于 Convert Entities 选择阶段关联面组与所属内环，不承担 Rebuild
 * 的拓扑重定位职责；优先使用权威子拓扑身份，旧数据才逐级回退。
 *
 * @param source topology 面来源。
 * @returns 可用于选择分组与来源屏蔽的稳定身份。
 */
export function resolveTopologyFaceReferenceSourceKey(
	source: TopologyFaceReferenceSourceDescriptor,
): string {
	// semanticName 只表示来源族，不能区分同族物理面；会话内最终回退当前面索引。
	const faceIdentity = source.locator.subEntityId
		?? source.locator.persistentRef
		?? `face-index:${source.locator.faceIndex}`;
	return `${resolveTopologySourceMemberScopeKey(source.owner)}::${faceIdentity}`;
}

/**
 * 解析 topology 边自身的稳定身份。
 *
 * @param locator topology 边定位信息。
 * @returns 边身份；优先使用稳定拓扑引用，最后才退回当前快照索引。
 */
export function resolveTopologyEdgeIdentityKey(locator: TopologyEdgeSourceLocator): string {
	return locator.subEntityId
		?? locator.persistentRef
		?? `edge-index:${locator.edgeIndex}`;
}

/**
 * 解析外部几何源的稳定键。
 * 说明：
 * 1. Convert Entities 的 UI 选择、preview 与 create 都需要用同一把键关联来源标签；
 * 2. 键生成规则必须放在 cad-core 模型层，避免 web-client 与 UIBridge 各自拼接导致漂移；
 * 3. 该键只用于当前数据链路索引，不承担几何拓扑匹配职责。
 * 4. canonical 边身份负责重定位，当前结果成员的 edgeIndex 负责区分共享端点的不同物理边。
 *
 * @param descriptor 外部几何源描述。
 * @returns 可用于 Map/Record 的稳定键。
 */
export function resolveExternalGeometrySourceDescriptorKey(
	descriptor: ExternalGeometrySourceDescriptor,
): string {
	if (descriptor.kind === 'sketch-entity') {
		const subEntityKey = descriptor.subEntity
			? `::${descriptor.subEntity.kind}::${descriptor.subEntity.edgeIndex}`
			: '';
		return `sketch-entity::${descriptor.sourceSketchId}::${descriptor.entityId}${subEntityKey}`;
	}

	if (descriptor.kind === 'feature-edge') {
		return `feature-edge::${descriptor.locator.owningObjectId}::${descriptor.locator.featureEdgeIndex}`;
	}

	return `topology-edge::${resolveTopologySourceMemberScopeKey(descriptor.owner)}::${resolveTopologyEdgeIdentityKey(descriptor.locator)}::edge-index:${descriptor.locator.edgeIndex}`;
}

/**
 * 解析外部来源描述的展示与诊断引用 ID。
 *
 * @param descriptor 外部几何来源描述。
 * @returns 可写入 diagnostic / relation runtime 的来源引用 ID。
 */
export function resolveExternalGeometrySourceRefEntityId(
	descriptor: ExternalGeometrySourceDescriptor,
): string {
	if (descriptor.kind === 'sketch-entity') {
		if (descriptor.subEntity) {
			return `${descriptor.entityId}/${descriptor.subEntity.kind}:${descriptor.subEntity.edgeIndex}`;
		}
		return descriptor.entityId;
	}

	if (descriptor.kind === 'feature-edge') {
		return `${descriptor.locator.owningObjectId}/${descriptor.locator.featureEdgeIndex}`;
	}

	const bodyKey = descriptor.owner.bodyBindingId ? `:${descriptor.owner.bodyBindingId}` : '';
	const modelBodyKey = descriptor.owner.modelBodyId ? `:model-body:${descriptor.owner.modelBodyId}` : '';
	const revisionKey = descriptor.owner.modelBodyRevisionId
		? `:model-body-revision:${descriptor.owner.modelBodyRevisionId}`
		: '';
	return `${descriptor.owner.ownerKind}:${descriptor.owner.ownerId}${bodyKey}${modelBodyKey}${revisionKey}/${resolveTopologyEdgeIdentityKey(descriptor.locator)}`;
}

/**
 * 解析外部来源描述的创建批次分组 key。
 *
 * @param descriptor 外部几何来源描述。
 * @returns 用于拆分创建批次的 owner key。
 */
export function resolveExternalGeometrySourceOwnerGroupKey(
	descriptor: ExternalGeometrySourceDescriptor,
): string {
	if (descriptor.kind === 'sketch-entity') {
		return `sketch-entity::${descriptor.sourceSketchId}`;
	}

	if (descriptor.kind === 'feature-edge') {
		return `feature-edge::${descriptor.locator.owningObjectId}`;
	}

	return `topology-edge::${resolveTopologySourceOwnerIdentityKey(descriptor.owner)}`;
}

/**
 * 解析来源边的用户可读编号。
 *
 * @param descriptor 外部几何来源描述。
 * @returns 边线编号，从 1 开始；非边来源返回 null。
 */
export function resolveExternalGeometrySourceEdgeDisplayIndex(
	descriptor: ExternalGeometrySourceDescriptor,
): number | null {
	if (descriptor.kind === 'feature-edge') {
		return descriptor.locator.featureEdgeIndex + 1;
	}

	if (descriptor.kind === 'topology-edge') {
		return descriptor.locator.edgeIndex + 1;
	}

	return null;
}
